import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { api } from '../services/api';
import { resolveItemImageUrl } from '../utils/tenantAssetUrls';
import { computeUnitEstimate, unitEstimateLabel, formatMoney, isPackageMode, isBulkMode, computeBulkLineTotal, BULK_WEIGHT_MIN, BULK_WEIGHT_STEP, MAX_BULK_LINE_WEIGHT, unitLabel } from '../utils/weightEstimate';
import './ProductDetail.css';

// Neutral inline placeholder used when a product has no image. Must NOT be a
// specific tenant's hosted image — the old code hardcoded a map of Hood Family
// Farms Squarespace CDN URLs, which leaked HFF photos onto every tenant's
// storefront (C-1 brand-leak fix). Inline SVG data-URI = no file dependency.
const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect width='400' height='400' fill='%23f0ece4'/%3E%3Ctext x='50%25' y='50%25' fill='%23a89f8d' font-family='sans-serif' font-size='22' text-anchor='middle' dominant-baseline='middle'%3ENo image%3C/text%3E%3C/svg%3E";

const getProductImage = (product) => resolveItemImageUrl(product.image_url) || PLACEHOLDER_IMAGE;

function ProductDetail() {
  const { id } = useParams();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [quantity, setQuantity] = useState(1);
  const [bulkWeight, setBulkWeight] = useState('');
  const [bulkWeightError, setBulkWeightError] = useState(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);

  // Fetch product on mount. Routed through api.get so X-Tenant-ID is attached —
  // a raw fetch here previously sent no tenant context, so on a non-default
  // tenant the item wouldn't resolve ("Failed to fetch product").
  //
  // channel=ecommerce (migration 149): match the storefront listing's channel
  // scope. An item that is active but NOT opted in to the eCommerce channel is
  // 404 on this channel — the backend channel guard returns "Item not found",
  // which surfaces here as the normal Product-Not-Found state. This is correct:
  // such an item isn't listed on the storefront either, so a deep link to it
  // should not bypass the channel gate.
  useEffect(() => {
    async function fetchProduct() {
      setLoading(true);
      setError(null);
      
      try {
        const response = await api.get(`/items/${id}?channel=ecommerce`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Product not found');
          }
          throw new Error('Failed to fetch product');
        }
        
        const data = await response.json();
        setProduct(data.data);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    
    fetchProduct();
  }, [id]);

  // Product options (if available - could be variants, sizes, etc.)
  const options = product?.options || null;
  const selectedOption = options ? options[selectedOptionIndex] : null;
  const currentPrice = selectedOption ? selectedOption.price : (product?.price || 0);

  // Package = catch-weight estimate + billed later. Bulk = charge-now exact weight.
  const isPackage = isPackageMode(product);
  const isBulk = isBulkMode(product);
  const uom = unitLabel(product?.unit_of_measure);

  // Catch-weight estimate (F-WEIGHT-ESTIMATE Phase 2). Only 'package' items with
  // a positive avg_weight + UOM produce an estimate; bulk (and a package missing
  // its avg_weight) fall back to the interim "price at weighing" presentation.
  // The estimate is built from the SELECTED price (member/variant aware) so the
  // shown estimate matches the rate displayed above it.
  const estimateInput = product
    ? { ...product, price: currentPrice }
    : null;
  const estimate = computeUnitEstimate(estimateInput);
  const estimateLabel = unitEstimateLabel(estimateInput);
  const bulkLineTotal = isBulk ? computeBulkLineTotal(estimateInput, bulkWeight) : null;

  const handleQuantityChange = (delta) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  const validateBulkWeight = (raw) => {
    const w = Number(raw);
    if (!Number.isFinite(w) || w <= 0) {
      return 'Enter a weight greater than 0';
    }
    if (w < BULK_WEIGHT_MIN) {
      return `Minimum weight is ${BULK_WEIGHT_MIN} ${uom}`;
    }
    if (w > MAX_BULK_LINE_WEIGHT) {
      return `Maximum weight is ${MAX_BULK_LINE_WEIGHT} ${uom} per line`;
    }
    return null;
  };

  const handleAddToCart = () => {
    if (!product) return;

    let cartQuantity = quantity;
    if (isBulk) {
      const err = validateBulkWeight(bulkWeight);
      if (err) {
        setBulkWeightError(err);
        return;
      }
      cartQuantity = Math.round(Number(bulkWeight) * 100) / 100;
    }
    
    const productImage = getProductImage(product);
    // Cart enrichment (Feature C C-4.1 / F-C1-10): carry the per-item fields
    // that downstream split-cart checkout (C-4.2), per-item tax, and shipping-
    // by-zone (C-4.3) need. These are captured at add-to-cart time from the
    // catalog item so the cart is self-describing and the storefront does not
    // have to re-fetch the item at checkout.
    //   sold_by         — 'each' | 'package' | 'bulk' (drives the deterministic
    //                     vs. packaged-weight split in /site/checkout)
    //   unit_of_measure — 'lb' | 'oz' | 'kg' | 'g' | null (weight UOM)
    //   is_taxable      — per-item taxability (overrides the blanket cart tax)
    //   tax_rate        — per-item snapshot rate (fallback handled server-side)
    //   shipping_zone   — 'not-shippable' | 'in-state' | 'in-country' |
    //                     'no-restrictions' (drives shipping-by-zone resolver)
    const cartProduct = {
      id: product.id,
      name: product.name,
      price: currentPrice,
      images: [productImage],
      image: productImage,
      sold_by: product.sold_by || 'each',
      unit_of_measure: product.unit_of_measure || null,
      // avg_weight (F-WEIGHT-ESTIMATE Phase 2): carried into the cart so Cart/
      // Checkout can show the catch-weight estimate without re-fetching. Only
      // 'package' items have a value; null otherwise. Display-only.
      avg_weight: product.avg_weight !== undefined && product.avg_weight !== null
        ? Number(product.avg_weight)
        : null,
      is_taxable: product.is_taxable !== undefined ? Boolean(product.is_taxable) : true,
      tax_rate: product.tax_rate !== undefined && product.tax_rate !== null
        ? Number(product.tax_rate)
        : null,
      shipping_zone: product.shipping_zone || 'no-restrictions',
    };
    
    addToCart(cartProduct, cartQuantity, selectedOption);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  // Determine if product is in stock
  const isInStock = product ? (
    product.item_type !== 'inventory' || 
    (product.inventory_quantity && product.inventory_quantity > 0)
  ) : false;

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="error-state">
            <h2>Product Not Found</h2>
            <p>{error || 'This product could not be found.'}</p>
            <Link to="/shopping" className="btn btn-primary">
              Back to Shop
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Parse details from description (one bullet per non-empty line).
  const details = product.description
    ? product.description.split('\n').filter(line => line.trim())
    : [];

  return (
    <div className="product-detail-page">
      <div className="container">
        {/* Breadcrumbs */}
        <nav className="breadcrumbs">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/shopping">Shop</Link>
          {product.category_name && (
            <>
              <span>/</span>
              <Link to={`/shopping?category=${product.category_slug || product.category_name}`}>
                {product.category_name}
              </Link>
            </>
          )}
          <span>/</span>
          <span>{product.name}</span>
        </nav>

        <div className="product-detail-grid">
          {/* Product Images */}
          <div className="product-images">
            <div className="main-image">
              <img 
                src={getProductImage(product)} 
                alt={product.name} 
              />
              {!isInStock && (
                <div className="sold-out-overlay">
                  <span>Sold Out</span>
                </div>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="product-info-detail">
            <h1>{product.name}</h1>
            
            {/* Price Display. Package and bulk catalog prices are per-UOM rates. */}
            <div className="price-container">
              {product.member_price && Number(product.member_price) < Number(product.price) ? (
                <>
                  <p className="price member-price-display">
                    ${Number(product.member_price).toFixed(2)}
                    {(isPackage || isBulk) && <span className="price-unit">/ {uom}</span>}
                    <span className="price-label">Member Price</span>
                  </p>
                  <p className="regular-price-display">
                    Regular: ${Number(product.price).toFixed(2)}{(isPackage || isBulk) ? ` / ${uom}` : ''}
                  </p>
                </>
              ) : (
                <p className="price">
                  ${Number(currentPrice).toFixed(2)}
                  {(isPackage || isBulk) && <span className="price-unit">/ {uom}</span>}
                </p>
              )}
            </div>

            {/* Package-only catch-weight estimate notice (billed later at weighing). */}
            {isPackage && (
              <div className="weighed-pricing-notice">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M12 3v18" />
                  <path d="M5 7h14" />
                  <path d="M5 7l-3 7a4 4 0 0 0 6 0z" />
                  <path d="M19 7l-3 7a4 4 0 0 0 6 0z" />
                  <path d="M8 21h8" />
                </svg>
                <div>
                  <strong>Priced by weight</strong>
                  {estimate.hasEstimate ? (
                    <>
                      <p className="weighed-estimate-amount">
                        Estimated {formatMoney(estimate.unitEstimate)} per package
                      </p>
                      <p className="weighed-estimate-basis">
                        {estimateLabel}
                      </p>
                      <p>
                        We weigh each package before fulfillment, so the final
                        price is set at weighing — this estimate is based on a
                        typical package weight and you won't be charged a fixed
                        amount at checkout.
                      </p>
                    </>
                  ) : (
                    <p>
                      This item is sold at {formatMoney(currentPrice)} per {uom}.
                      We weigh each order before fulfillment, so the final price
                      is set at weighing — you won't be charged a fixed amount at
                      checkout.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Bulk: exact weight entry with live line total (charge now). */}
            {isBulk && (
              <div className="bulk-pricing-notice">
                <p className="bulk-rate-line">
                  {formatMoney(currentPrice)} / {uom}
                </p>
                {bulkLineTotal !== null && (
                  <p className="bulk-line-total">
                    = {formatMoney(bulkLineTotal)}
                  </p>
                )}
                <p className="bulk-pricing-hint">
                  Enter the weight you want. You'll pay the exact total at checkout.
                </p>
              </div>
            )}

            {product.description && (
              <p className="description">{product.description}</p>
            )}

            {/* Product Options (if available) */}
            {options && options.length > 0 && (
              <div className="product-options">
                <label htmlFor="product-size">Size</label>
                <select 
                  id="product-size"
                  value={selectedOptionIndex}
                  onChange={(e) => setSelectedOptionIndex(parseInt(e.target.value))}
                >
                  {options.map((option, index) => (
                    <option key={index} value={index}>
                      {option.name} - ${option.price.toFixed(2)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Quantity (each/package) or weight (bulk) */}
            {isBulk ? (
              <div className="quantity-selector bulk-weight-selector">
                <label htmlFor="bulk-weight">Weight ({uom})</label>
                <input
                  id="bulk-weight"
                  type="number"
                  className="bulk-weight-input"
                  value={bulkWeight}
                  min={BULK_WEIGHT_MIN}
                  max={MAX_BULK_LINE_WEIGHT}
                  step={BULK_WEIGHT_STEP}
                  placeholder="e.g. 2.5"
                  onChange={(e) => {
                    setBulkWeight(e.target.value);
                    setBulkWeightError(null);
                  }}
                />
                {bulkWeightError && (
                  <p className="bulk-weight-error">{bulkWeightError}</p>
                )}
              </div>
            ) : (
              <div className="quantity-selector">
                <label>Quantity</label>
                <div className="quantity-controls">
                  <button 
                    className="qty-btn" 
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <input 
                    type="number" 
                    value={quantity} 
                    min="1" 
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  />
                  <button 
                    className="qty-btn"
                    onClick={() => handleQuantityChange(1)}
                  >
                    +
                  </button>
                </div>
                {isPackage && (
                  <p className="quantity-weighed-hint">
                    Quantity = number of packages. Each is priced by its weight.
                  </p>
                )}
              </div>
            )}

            {/* Stock Status */}
            {product.item_type === 'inventory' && isInStock && product.inventory_quantity <= 5 && (
              <p className="low-stock-warning">
                Only {product.inventory_quantity} left in stock!
              </p>
            )}

            {/* Add to Cart */}
            <button 
              className={`btn btn-primary btn-lg add-to-cart ${!isInStock ? 'disabled' : ''} ${addedToCart ? 'added' : ''}`}
              disabled={!isInStock}
              onClick={handleAddToCart}
            >
              {!isInStock 
                ? 'Sold Out' 
                : addedToCart 
                  ? '✓ Added to Cart!' 
                  : 'Add to Cart'}
            </button>

            {/* Product Details */}
            {details.length > 0 && (
              <div className="product-details">
                <h3>Details</h3>
                <ul>
                  {details.map((detail, index) => (
                    <li key={index}>{detail}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Shipping Info */}
            <div className="shipping-info">
              <h3>Shipping</h3>
              <p>
                {product.shipping_zone === 'not-shippable' 
                  ? 'Local pickup only - not available for shipping'
                  : product.shipping_zone === 'in-state'
                    ? 'Available for shipping within our state'
                    : product.shipping_zone === 'in-country'
                      ? 'Available for shipping within the United States'
                      : 'Ships worldwide'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
